import { type Theme as MuiTheme, createTheme } from '@mui/material/styles';

import type { Theme as BaseTheme } from '@kausal/themes/types';

import { getComponents } from './components';
import { getPalette } from './palette';
import { getTypography } from './typography';

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Theme extends BaseTheme {}
}

declare module '@emotion/react' {
  interface Theme extends BaseTheme, MuiTheme {}
}

/**
 * Builds the MUI theme for a single base theme.
 *
 * This must stay a pure function of `theme`. Plans that share a hostname and
 * differ only by basePath are reached through client-side navigation, so one
 * browser document can render several themes in turn; caching the result here
 * would pin every later plan to whichever theme loaded first.
 */
export function initializeMuiTheme(theme: BaseTheme): MuiTheme {
  return {
    ...theme,
    ...createTheme({
      // MUI's defaults, except:
      // - md: 768px is the common mobile/desktop switch point (Bootstrap, Tailwind),
      //   where MUI's 900px collapsed layouts too early;
      // - xl: 1400px, as Bootstrap's widest step. MUI's 1536px made the widest
      //   container too wide to lay content out cleanly.
      // Keep in sync with $grid-breakpoints in styles/_theme-variables.scss.
      breakpoints: {
        values: { xs: 0, sm: 600, md: 768, lg: 1200, xl: 1400 },
      },
      // Shadows are modified from the MUI base theme (https://mui.com/material-ui/customization/default-theme/)
      // simply reducing the opacity
      shadows: [
        'none',
        '0px 2px 1px -1px rgba(0,0,0,0.08), 0px 1px 1px 0px rgba(0,0,0,0.06), 0px 1px 3px 0px rgba(0,0,0,0.05)',
        '0px 3px 1px -2px rgba(0,0,0,0.08), 0px 2px 2px 0px rgba(0,0,0,0.06), 0px 1px 5px 0px rgba(0,0,0,0.05)',
        '0px 3px 3px -2px rgba(0,0,0,0.08), 0px 3px 4px 0px rgba(0,0,0,0.06), 0px 1px 8px 0px rgba(0,0,0,0.05)',
        '0px 2px 4px -1px rgba(0,0,0,0.08), 0px 4px 5px 0px rgba(0,0,0,0.06), 0px 1px 10px 0px rgba(0,0,0,0.05)',
        '0px 3px 5px -1px rgba(0,0,0,0.08), 0px 5px 8px 0px rgba(0,0,0,0.06), 0px 1px 14px 0px rgba(0,0,0,0.05)',
        '0px 3px 5px -1px rgba(0,0,0,0.08), 0px 6px 10px 0px rgba(0,0,0,0.06), 0px 1px 18px 0px rgba(0,0,0,0.05)',
        '0px 4px 5px -2px rgba(0,0,0,0.08), 0px 7px 10px 1px rgba(0,0,0,0.06), 0px 2px 16px 1px rgba(0,0,0,0.05)',
        '0px 5px 5px -3px rgba(0,0,0,0.08), 0px 8px 10px 1px rgba(0,0,0,0.06), 0px 3px 14px 2px rgba(0,0,0,0.05)',
        '0px 5px 6px -3px rgba(0,0,0,0.08), 0px 9px 12px 1px rgba(0,0,0,0.06), 0px 3px 16px 2px rgba(0,0,0,0.05)',
        '0px 6px 6px -3px rgba(0,0,0,0.08), 0px 10px 14px 1px rgba(0,0,0,0.06), 0px 4px 18px 3px rgba(0,0,0,0.05)',
        '0px 6px 7px -4px rgba(0,0,0,0.08), 0px 11px 15px 1px rgba(0,0,0,0.06), 0px 4px 20px 3px rgba(0,0,0,0.05)',
        '0px 7px 8px -4px rgba(0,0,0,0.08), 0px 12px 17px 2px rgba(0,0,0,0.06), 0px 5px 22px 4px rgba(0,0,0,0.05)',
        '0px 7px 8px -4px rgba(0,0,0,0.08), 0px 13px 19px 2px rgba(0,0,0,0.06), 0px 5px 24px 4px rgba(0,0,0,0.05)',
        '0px 7px 9px -4px rgba(0,0,0,0.08), 0px 14px 21px 2px rgba(0,0,0,0.06), 0px 5px 26px 4px rgba(0,0,0,0.05)',
        '0px 8px 9px -5px rgba(0,0,0,0.08), 0px 15px 22px 2px rgba(0,0,0,0.06), 0px 6px 28px 5px rgba(0,0,0,0.05)',
        '0px 8px 10px -5px rgba(0,0,0,0.08), 0px 16px 24px 2px rgba(0,0,0,0.06), 0px 6px 30px 5px rgba(0,0,0,0.05)',
        '0px 8px 11px -5px rgba(0,0,0,0.08), 0px 17px 26px 2px rgba(0,0,0,0.06), 0px 6px 32px 5px rgba(0,0,0,0.05)',
        '0px 9px 11px -5px rgba(0,0,0,0.08), 0px 18px 28px 2px rgba(0,0,0,0.06), 0px 7px 34px 6px rgba(0,0,0,0.05)',
        '0px 9px 12px -6px rgba(0,0,0,0.08), 0px 19px 29px 2px rgba(0,0,0,0.06), 0px 7px 36px 6px rgba(0,0,0,0.05)',
        '0px 10px 13px -6px rgba(0,0,0,0.08), 0px 20px 31px 3px rgba(0,0,0,0.06), 0px 8px 38px 7px rgba(0,0,0,0.05)',
        '0px 10px 13px -6px rgba(0,0,0,0.08), 0px 21px 33px 3px rgba(0,0,0,0.06), 0px 8px 40px 7px rgba(0,0,0,0.05)',
        '0px 10px 14px -6px rgba(0,0,0,0.08), 0px 22px 35px 3px rgba(0,0,0,0.06), 0px 8px 42px 7px rgba(0,0,0,0.05)',
        '0px 11px 14px -7px rgba(0,0,0,0.08), 0px 23px 36px 3px rgba(0,0,0,0.06), 0px 9px 44px 8px rgba(0,0,0,0.05)',
        '0px 11px 15px -7px rgba(0,0,0,0.08), 0px 24px 38px 3px rgba(0,0,0,0.06), 0px 9px 46px 8px rgba(0,0,0,0.05)',
      ],
      palette: getPalette(theme),
      typography: getTypography(theme),
      shape: {
        // The base border radius, used as a multiplier when setting borderRadius on components
        borderRadius: theme.spaces.s025,
      },
      spacing: theme.space,
      components: getComponents(theme),
    }),
  };
}
